import * as React from 'react';
import { stringHasInteger } from '@common/utils/string';
import { I18n } from 'react-redux-i18n';
import { SpreadsheetImportType } from '@modules/spreadsheets/dto/spreadsheet/import-type';
import { SpreadsheetDataImportResponseDto } from '@modules/spreadsheets/dto/data-import/response';
import { SpreadsheetDetailsImportDataParams, SmartColumn, SpreadsheetColumnModelDto, SpreadsheetImportSettings } from '@modules/spreadsheets/dto/spreadsheet/type';
import { getImportProgress } from '@modules/spreadsheets/actions/spreadsheet';
import { cleanSmartColumnsForApi } from '../spreadsheet-result/components/table/data-converter';

export type DataImportProps = {
  close: () => void;
  showErrorMessage: (message: string) => void;
  onImport: (data: SpreadsheetDetailsImportDataParams) => Promise<SpreadsheetDataImportResponseDto>;
  columnModel: SpreadsheetColumnModelDto;
  pss: boolean;
  onImportComplete?: () => void;
};

export const useController = (props: DataImportProps) => {
  const DEFAULT_DELIMITER = '';
  
  const IMPORT_MIME_TYPE = {
    [SpreadsheetImportType.tab]: '.txt',
    [SpreadsheetImportType.comma]: '.csv,.txt',
    [SpreadsheetImportType.other]: '.txt'
  };

  enum BlankCellOption {
    importBlankCells = 'importBlankCells',
    ignoreBlankCells = 'ignoreBlankCells'
  }

  const { onImport, showErrorMessage, columnModel, pss, onImportComplete } = props;
  const [importResponseMetadata, setImportResponseMetadata] = React.useState<SpreadsheetDataImportResponseDto | undefined>();
  const [selectedCheckboxOptions, setSelectedCheckboxOptions] = React.useState<Array<string>>([]);
  const [selectedBlankCellOption, setSelectedBlankCellOption] = React.useState(BlankCellOption.importBlankCells);
  const [delimiter, setDelimiter] = React.useState(DEFAULT_DELIMITER);
  const [importing, setImportingState] = React.useState(false);
  const [matchedColumnsAmount, setMatchedColumnsAmount] = React.useState('1');
  const [uniqueImportNumber, setUniqueImportNumber] = React.useState<number | null>(null);
  const [progressInterval, setProgressInterval] = React.useState<NodeJS.Timeout | null>(null);
  const [fallbackProgress, setFallbackProgress] = React.useState(0);
  const uploadInputRef = React.useRef<HTMLInputElement>();

  const settings = React.useMemo(() => {
    return [
      { label: 'Tab Delimited', id: SpreadsheetImportType.tab },
      { label: 'Comma Delimited', id: SpreadsheetImportType.comma },
      { label: 'Other (specify delimiter)', id: SpreadsheetImportType.other },
    ].map((x) => ({ ...x, label: I18n.t(x.label) }));
  }, []);

  const checkboxOptions = React.useMemo(() => {
    return [
      { label: 'Import into tables only', metadata: { importIntoTablesOnly: true } },
    ].map((x) => ({ ...x, id: x.label, label: I18n.t(x.label) }));
  }, []);

  const blankCellsOptions = React.useMemo(() => {
    return [
      { label: 'Import blank cells over existing data', id: BlankCellOption.importBlankCells },
      { label: 'Ignore blank cells', id: BlankCellOption.ignoreBlankCells },
    ].map((x) => ({ ...x, label: I18n.t(x.label) }));
  }, []);

  const [selectedSetting, setSelectedSetting] = React.useState(settings[0]!.id);

  const handleSettingsChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    setSelectedSetting(value as SpreadsheetImportType);

    setDelimiter(value === SpreadsheetImportType.other ? '' : DEFAULT_DELIMITER);
  }, []);

  const handleCheckboxOptionsChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    const resultSettings = selectedCheckboxOptions.includes(value)
      ? selectedCheckboxOptions.filter((x) => x !== value)
      : [...selectedCheckboxOptions, value];

    setSelectedCheckboxOptions(resultSettings);
  }, [selectedCheckboxOptions]);

  const handleBlankCellOptionChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    setSelectedBlankCellOption(value as BlankCellOption);
  }, []);

  const handleDelimiterChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    setDelimiter(value);
  }, []);

  // Cleanup progress interval on unmount
  React.useEffect(() => {
    return () => {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
    };
  }, [progressInterval]);


  const handleClose = React.useCallback(() => {
    if (progressInterval) {
      clearInterval(progressInterval);
      setProgressInterval(null);
    }
    setImportingState(false);
    setImportResponseMetadata(undefined);
    setUniqueImportNumber(null);
    props.close();
  }, [progressInterval, props]);

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = e.target.files && e.target.files[0];

      e.target.value = '';

      if (!file) {
        return;
      }

      // Validate column model
      if (!columnModel || !columnModel.smartColumns || columnModel.smartColumns.length === 0) {
        showErrorMessage(I18n.t('Invalid column model. Please ensure the spreadsheet has columns configured.'));
        return;
      }

      // Include all columns (including previously filtered ones)
      const validColumns = columnModel.smartColumns.filter((col: SmartColumn) => {
        if (!col.field) return false;
        
        const fieldId = col.field.fieldid;
        const fieldName = col.field.fieldname;
        const columnOrder = col.columnOrder;
        
        // Only filter out columns with no field name or empty field name
        return fieldName && fieldName !== "";
      }).map((col: SmartColumn) => ({
        ...col,
        // Add missing id and type fields at column level
        id: col.field?.fieldid || 0,
        type: col.field?.type || col.field?.fieldtype?.typeid || -100,
        // Ensure width is present (default to 100 if not specified)
        width: col.width || 100,
        editable: col.editable,
        field: {
          ...col.field,
          // Ensure all required fields are present to match expected payload structure
          dataSourceNodeId: col.field.dataSourceNodeId || 1,
          fieldid: col.field.fieldid,
          fieldname: col.field.fieldname,
          fieldnameWithPath: col.field.fieldnameWithPath || col.field.fieldname,
          fieldtype: col.field.fieldtype ? {
            typeid: col.field.fieldtype.typeid,
            typename: col.field.fieldtype.typename
          } : undefined,
          system: col.field.system || false,
          systemField: col.field.system || false,
          tableIndex: col.field.tableIndex || 0,
          type: col.field.type || col.field.fieldtype?.typeid || -100
        }
      }));

      if (validColumns.length === 0) {
        showErrorMessage(I18n.t('No valid columns found for import. Please configure the spreadsheet columns first.'));
        return;
      }

      // Clean columns before sending to API (remove dropdown/lookup values, etc.)
      const cleanedColumns = cleanSmartColumnsForApi(validColumns);

      setImportingState(true);
      setFallbackProgress(0);
      // Don't set initial metadata - wait for actual data from API to show progress bar
      // This prevents the progress bar from jumping from 0 to 100%

      const optionsSubmitData = selectedCheckboxOptions.reduce((acc, id) => {
        const option = checkboxOptions.find((x) => x.id === id);

        if (option) {
          return {
            ...acc,
            ...option.metadata
          };
        }

        return acc;
      }, {});

      // Generate unique import number within Java int range
      const importNumber = Math.floor(Math.random() * 1000000) + 1;
      setUniqueImportNumber(importNumber);

      // Construct import settings
      // Merge checkbox-derived options into settings
      const importSettings: SpreadsheetImportSettings = {
        pss: pss,
        delimiter: selectedSetting === SpreadsheetImportType.tab ? 'TAB' : 
                   selectedSetting === SpreadsheetImportType.comma ? 'COMMA' : 
                   delimiter || 'TAB',
        numberOfUniqueIdentifiers: Number(matchedColumnsAmount),
        // default false, can be overridden by optionsSubmitData
        importIntoTablesOnly: false,
        importBlankCellsOption: selectedBlankCellOption === BlankCellOption.importBlankCells ? 1 : 2,
        uniqueImportNumber: importNumber,
        columnModel: {
          smartColumns: cleanedColumns.map(col => ({
            ...col,
            securityEditable: true,
            editable: col.editable ?? false
          }))
        },
        // spread any option metadata (e.g., importIntoTablesOnly)
        ...(optionsSubmitData as Partial<SpreadsheetImportSettings>)
      };

      const response = await onImport({
        fileData: file,
        settings: importSettings,
        importType: selectedSetting,
        nbMatchedColumns: Number(matchedColumnsAmount)
      });

      // If the import started but didn't complete, start polling for progress
      if (response.completed === false || response.needsProgressCheck) {
        const interval = setInterval(async () => {
          try {
            const progressData = await getImportProgress(importNumber);
            
            // Only set metadata when we have valid data from API (total > 0)
            // This ensures progress bar shows accurate percentage from the start
            if (progressData && progressData.total > 0) {
              setImportResponseMetadata(progressData);
            }
            
            // Update fallback progress if we don't have specific progress data
            if (!progressData.processed && !progressData.total) {
              setFallbackProgress(prev => Math.min(90, prev + 5));
            }
            
            if (progressData.completed) {
              setFallbackProgress(100);
              clearInterval(interval);
              setProgressInterval(null);
              setImportingState(false); // Set importing to false only when completed
              // Call onImportComplete callback when import is completed
              if (onImportComplete) {
                onImportComplete();
              }
            }
          } catch (error) {
            console.error('Progress polling error:', error);
            clearInterval(interval);
            setProgressInterval(null);
            setImportingState(false); // Set importing to false on error
          }
        }, 500); // Poll every 500ms for better performance
        
        setProgressInterval(interval);
        // Don't set importing to false here - keep it true while polling
      } else {
        // Only set metadata if we have valid data (total > 0)
        // This ensures progress bar shows accurate percentage from the start
        if (response && response.total > 0) {
          setImportResponseMetadata(response);
        }
        setFallbackProgress(100);
        setImportingState(false); // Set importing to false for immediate completion
        // Call onImportComplete callback for immediate completion
        if (onImportComplete) {
          onImportComplete();
        }
      }
    } catch (err) {
      // Show the actual error message if available, otherwise show generic message
      const errorMessage = err instanceof Error ? err.message : 
                          (err as { message?: string; error?: string })?.message || 
                          (err as { message?: string; error?: string })?.error || 
                          I18n.t('Failed to import data');
      showErrorMessage(`Import failed: ${errorMessage}`);
      setImportingState(false); // Set importing to false on error
    }
    // Remove the finally block that was setting importingState to false
  };

  const handleMatchedColumnsAmountChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const isValid = stringHasInteger(value, { allowEmpty: true, allowNegative: false, allowZero: false });

    if (isValid) {
      setMatchedColumnsAmount(value);
    }
  }, []);

  const handleImportBtnClick = React.useCallback(() => {
    uploadInputRef.current?.click();
  }, [uploadInputRef]);

  const exceptions = React.useMemo(() => {
    const value: Array<string> = [];

    if (importResponseMetadata?.errors?.length) {
      value.push(...importResponseMetadata.errors);
    }

    if (importResponseMetadata?.warnings?.length) {
      value.push(...importResponseMetadata.warnings);
    }

    return value;
  }, [importResponseMetadata]);

  const shouldShowFeedback = React.useMemo(() => {
    return (
      importing || 
      Boolean(exceptions.length) || 
      (importResponseMetadata 
        ? (importResponseMetadata.total === 0 || importResponseMetadata.total > 0)
        : false)
    );
  }, [importing, exceptions, importResponseMetadata]);

  const importBtnText = React.useMemo(() => {
    if (shouldShowFeedback) {
      return I18n.t('Import more');
    }

    return I18n.t('Import');
  }, [shouldShowFeedback]);


  const importStatusMessage = React.useMemo(() => {
    if (!importResponseMetadata) {
      return '';
    }
    
    if (pss) {
      return I18n.t('Importing pedigree data');
    } else {
      return I18n.t('Importing individual data');
    }
  }, [importResponseMetadata, pss]);

  const rowsImportedMessage = React.useMemo(() => {
    if (!importResponseMetadata) {
      return '';
    }
    
    return I18n.t('Rows imported', {
      processed: importResponseMetadata.processed,
      total: importResponseMetadata.total
    });
  }, [importResponseMetadata]);

  return {
    handleBlankCellOptionChange,
    selectedBlankCellOption,
    blankCellsOptions,
    IMPORT_MIME_TYPE,
    settings,
    checkboxOptions,
    selectedSetting,
    selectedCheckboxOptions,
    delimiter,
    importing,
    matchedColumnsAmount,
    uploadInputRef,
    importResponseMetadata,
    fallbackProgress,
    handleSettingsChange,
    handleCheckboxOptionsChange,
    handleDelimiterChange,
    handleImport,
    handleMatchedColumnsAmountChange,
    handleImportBtnClick,
    exceptions,
    shouldShowFeedback,
    importBtnText,
    importStatusMessage,
    rowsImportedMessage,
    handleClose
  };
};